import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import LoadingIndicator from './LoadingIndicator';

const BandwidthChart = ({ data }) => {
    const convertDataToDynamicUnit = (data) => {
      // Find the maximum value among 'uprate' and 'downrate'
      const maxValue = Math.max(
        ...data.map(item => item.uprate),
        ...data.map(item => item.downrate)
      );
  
      let conversionFactor = 1; // Default is bits per second (bps)
      let unitLabel = 'bps';
  
      // Determine conversion factor based on the highest value
      if (maxValue >= 1000000000) { // If greater than or equal to 1 Gbps
        conversionFactor = 1 / 1000000000;
        unitLabel = 'Gbps';
      } else if (maxValue >= 1000000) { // If greater than or equal to 1 Mbps
        conversionFactor = 1 / 1000000;
        unitLabel = 'Mbps';
      } else if (maxValue >= 1000) { // If greater than or equal to 1 kbps
        conversionFactor = 1 / 1000;
        unitLabel = 'kbps';
      }
  
      // Convert data to the chosen unit
      const convertedData = data.map(item => ({
        ...item,
        uprate: item.uprate * conversionFactor,
        downrate: item.downrate * conversionFactor,
        unitLabel: unitLabel
      }));
  
      return { convertedData, unitLabel };
    };
  
    // Invoke the conversion function
    const { convertedData, unitLabel } = convertDataToDynamicUnit(data);

  const CustomTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
          const formattedDate = new Date(payload[0].payload.timestamp).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
          });

          return (
              <div className="custom-tooltip" style={{ backgroundColor: '#fff', padding: '10px', borderRadius: '5px', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                  <p className="label" style={{ margin: 0 }}>{`${formattedDate}`}</p>
                  <p className="intro" style={{ color: '#8884d8', margin: 0 }}>{`Upload: ${payload[0].value.toFixed(2)} ${unitLabel}`}</p> {/* Upload value */}
                  <p className="intro" style={{ color: '#82ca9d', margin: 0 }}>{`Download: ${payload[1].value.toFixed(2)} ${unitLabel}`}</p> {/* Download value */}
              </div>
          );
      }
      return null;
  };

  return (
    <React.Fragment>
      {(!data || data.length === 0) && 
        <LoadingIndicator className="text-center text-secondary" message="Loading bandwidth data ..." />
      }
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={convertedData} margin={{ top: 20, right: 3, left: 3, bottom: 20 }} >
          <CartesianGrid strokeDasharray="3 3" />
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#82ca9d" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <XAxis 
            dataKey="timestamp" 
            tickFormatter={(timestamp) => new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            // reversed
          />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {/* <Area type="monotone" dataKey="uprate" stroke="#8884d8" activeDot={{ r: 8 }} />
          <Area type="monotone" dataKey="downrate" stroke="#82ca9d" /> */}
          <Area type="monotone" dataKey="uprate" name={`Upload (${unitLabel})`} stroke="#8884d8" fillOpacity={1} fill="url(#colorUv)" />
          <Area type="monotone" dataKey="downrate" name={`Download (${unitLabel})`} stroke="#82ca9d" fillOpacity={1} fill="url(#colorPv)" />
        </AreaChart>
      </ResponsiveContainer>
    </React.Fragment>
  );
};

export default BandwidthChart;
